import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        await verifyMagicLink(token);
        setStatus('success');
        toast.success('Successfully signed in!');
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } catch (error) {
        setStatus('error');
        setErrorMessage(error.response?.data?.detail || 'Invalid or expired link');
      }
    };

    verify();
  }, [token, verifyMagicLink, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-secondary/30">
      <Card className="w-full max-w-md text-center shadow-xl" data-testid="verify-page">
        <CardHeader>
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 animate-spin mx-auto text-primary mb-4" />
              <CardTitle>Verifying...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <CardTitle className="text-green-600">Welcome Back!</CardTitle>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
              <CardTitle className="text-red-600">Verification Failed</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <p className="text-muted-foreground">
              Please wait while we verify your magic link...
            </p>
          )}
          {status === 'success' && (
            <>
              <p className="text-muted-foreground">
                You've been signed in successfully. Redirecting to your dashboard...
              </p>
              <Button onClick={() => navigate('/')} className="gap-2" data-testid="go-to-dashboard">
                Go to Dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-muted-foreground">
                {errorMessage}
              </p>
              <p className="text-sm text-muted-foreground">
                Magic links expire after 15 minutes. Please request a new one.
              </p>
              <Button onClick={() => navigate('/login')} className="gap-2" data-testid="back-to-login">
                Back to Login
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
